// components/modals/servicehds/hooks/useServiceHDSModal.js
import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import ServiceHDSService from '../../../../services/ServiceDHSService';
import AuthService from '../../../../services/AuthService';

const useServiceHDSModal = (service, isOpen, onClose, isEditMode, onSave) => {
    // États principaux
    const [editedService, setEditedService] = useState(service);
    const [subServices, setSubServices] = useState([]);
    const [isEditing, setIsEditing] = useState(isEditMode);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // États pour les fichiers
    const [selectedFile, setSelectedFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [removeCurrentImage, setRemoveCurrentImage] = useState(false);
    const fileInputRef = useRef(null);

    // États pour les sous-services
    const [editingSubService, setEditingSubService] = useState(null);
    const [newSubService, setNewSubService] = useState({
        name: '',
        amount: '',
        frequency: 'LUNDI_A_VENDREDI'
    });
    const [isAddingSubService, setIsAddingSubService] = useState(false);

    // Fonction pour obtenir le username de l'utilisateur connecté
    const getCurrentUser = () => {
        const user = AuthService.getCurrentUser();
        return user?.username || 'Utilisateur inconnu';
    };

    // Récupérer les sous-services
    const fetchSubServices = async () => {
        if (!service?.id) return;

        try {
            const subServicesData = await ServiceHDSService.getSubServicesByServiceId(service.id);
            setSubServices(subServicesData);
        } catch (error) {
            console.error('Error fetching sub-services:', error);
            toast.error('Erreur lors du chargement des sous-services');
        }
    };

    // Réinitialiser les états lors de l'ouverture/fermeture
    useEffect(() => {
        if (service) {
            setEditedService(service);
            fetchSubServices();
        }
        setIsEditing(isEditMode);
        setSelectedFile(null);
        setImagePreview(null);
        setRemoveCurrentImage(false);
        setEditingSubService(null);
        setIsAddingSubService(false);
        setNewSubService({ name: '', amount: '', frequency: 'LUNDI_A_VENDREDI' });
    }, [service, isEditMode, isOpen]);

    // Validation du fichier
    const validateFile = (file) => {
        // Types autorisés (images uniquement)
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            toast.error(`Type de fichier non supporté. Types autorisés: JPEG, PNG, JPG, WEBP, GIF, SVG`);
            return false;
        }

        // Taille max: 10 Mo
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            toast.error(`Fichier trop volumineux. Maximum: 10 Mo (${(file.size / 1024 / 1024).toFixed(2)} Mo)`);
            return false;
        }

        return true;
    };

    // Sélection d'un fichier
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!validateFile(file)) {
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        setSelectedFile(file);
        setRemoveCurrentImage(false);

        // Créer un aperçu
        const reader = new FileReader();
        reader.onload = (e) => {
            setImagePreview(e.target.result);
        };
        reader.onerror = () => {
            toast.error('Erreur lors de la lecture du fichier');
        };
        reader.readAsDataURL(file);
    };

    // Suppression de l'image
    const handleRemoveImage = () => {
        setSelectedFile(null);
        setImagePreview(null);
        setRemoveCurrentImage(true);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Annuler la suppression de l'image
    const handleCancelRemoveImage = () => {
        setRemoveCurrentImage(false);
    };

    // Gestion des changements de champs
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedService(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Sauvegarde du service
    const handleSaveService = async () => {
        try {
            setIsSaving(true);

            // Préparer les données
            const updateData = {
                name: editedService.name,
                description: editedService.description,
                lastModifiedBy: getCurrentUser()
            };

            // Appel API avec ou sans fichier
            let updatedService;

            if (selectedFile) {
                // Upload avec fichier
                setIsUploading(true);
                updatedService = await ServiceHDSService.updateService(
                    editedService.id,
                    updateData,
                    selectedFile
                );
                setIsUploading(false);
                toast.success('Image uploadée avec succès');
            } else {
                // Mise à jour JSON sans fichier
                updatedService = await ServiceHDSService.updateServiceJson(
                    editedService.id,
                    updateData
                );
            }

            // Mettre à jour l'état local
            setEditedService(prevState => ({
                ...prevState,
                ...updatedService
            }));

            toast.success('Service mis à jour avec succès');

            // Réinitialiser les états d'image
            setSelectedFile(null);
            setImagePreview(null);
            setRemoveCurrentImage(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            setIsEditing(false);

            // Notifier le parent
            if (onSave) onSave();

        } catch (error) {
            console.error('Error updating service:', error);
            let errorMessage = 'Erreur lors de la mise à jour du service';
            if (error.response?.data?.message) {
                errorMessage += `: ${error.response.data.message}`;
            } else if (error.response?.data?.error) {
                errorMessage += `: ${error.response.data.error}`;
            } else if (error.message) {
                errorMessage += `: ${error.message}`;
            }
            toast.error(errorMessage);
        } finally {
            setIsSaving(false);
            setIsUploading(false);
        }
    };

    // Ajout d'un sous-service
    const handleAddSubService = async () => {
        if (!newSubService.name.trim() || !newSubService.amount || parseFloat(newSubService.amount) <= 0) {
            toast.error('Veuillez remplir tous les champs du sous-service');
            return;
        }

        try {
            setIsSaving(true);
            await ServiceHDSService.createSubService({
                name: newSubService.name,
                amount: parseFloat(newSubService.amount),
                frequency: newSubService.frequency,
                serviceHdsId: editedService.id
            });

            toast.success('Sous-service ajouté avec succès');
            setNewSubService({ name: '', amount: '', frequency: 'LUNDI_A_VENDREDI' });
            setIsAddingSubService(false);
            await fetchSubServices();
        } catch (error) {
            console.error('Error adding sub-service:', error);
            toast.error('Erreur lors de l\'ajout du sous-service');
        } finally {
            setIsSaving(false);
        }
    };

    // Mise à jour d'un sous-service
    const handleUpdateSubService = async () => {
        if (!editingSubService?.name.trim() || !editingSubService?.amount || parseFloat(editingSubService.amount) <= 0) {
            toast.error('Veuillez remplir tous les champs du sous-service');
            return;
        }

        try {
            setIsSaving(true);
            await ServiceHDSService.updateSubService(editingSubService.id, {
                name: editingSubService.name,
                amount: parseFloat(editingSubService.amount),
                frequency: editingSubService.frequency
            });

            toast.success('Sous-service modifié avec succès');
            setEditingSubService(null);
            await fetchSubServices();
        } catch (error) {
            console.error('Error updating sub-service:', error);
            toast.error('Erreur lors de la modification du sous-service');
        } finally {
            setIsSaving(false);
        }
    };

    // Suppression d'un sous-service
    const handleDeleteSubService = async (subServiceId, subServiceName) => {
        try {
            const result = window.confirm(`Êtes-vous sûr de vouloir supprimer le sous-service "${subServiceName}" ?`);
            if (result) {
                await ServiceHDSService.deleteSubService(subServiceId);
                toast.success('Sous-service supprimé avec succès');
                await fetchSubServices();
            }
        } catch (error) {
            console.error('Error deleting sub-service:', error);
            toast.error('Erreur lors de la suppression du sous-service');
        }
    };

    // Annuler l'édition
    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditedService(service);
        setSelectedFile(null);
        setImagePreview(null);
        setRemoveCurrentImage(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Gestion du clic sur le backdrop
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Fonction pour obtenir le libellé affichable de la fréquence
    const getFrequencyLabel = (frequency) => {
        const frequencyMap = {
            'LUNDI_A_VENDREDI': 'De lundi à vendredi',
            'CHAQUE_JOUR': 'Chaque jour',
            'TROIS_JOURS_PAR_SEMAINE': '3 jours par semaine',
            'HEURE': 'Par heure',
            'SEMAINE': 'Par semaine',
            'MOIS': 'Par mois',
            'MENSUEL': 'Par mois',
            'HEBDOMADAIRE': 'Par semaine'
        };
        return frequencyMap[frequency] || frequency;
    };

    // Déterminer quelle image afficher
    const getDisplayImage = () => {
        if (imagePreview) return imagePreview;
        if (removeCurrentImage) return null;
        return editedService?.mediaUrlPresigned || editedService?.mediaUrl || null;
    };

    // Vérifier si l'image actuelle est une URL présignée
    const hasPresignedUrl = () => {
        return !!editedService?.mediaUrlPresigned;
    };

    return {
        // États
        editedService,
        subServices,
        isEditing,
        isSaving,
        isUploading,
        selectedFile,
        imagePreview,
        removeCurrentImage,
        fileInputRef,
        editingSubService,
        newSubService,
        isAddingSubService,

        // Handlers
        handleInputChange,
        handleFileSelect,
        handleRemoveImage,
        handleCancelRemoveImage,
        handleSaveService,
        handleAddSubService,
        handleUpdateSubService,
        handleDeleteSubService,
        handleCancelEdit,
        handleBackdropClick,

        // Setters
        setRemoveCurrentImage,
        setIsEditing,
        setEditingSubService,
        setNewSubService,
        setIsAddingSubService,

        // Utilitaires
        getFrequencyLabel,
        getDisplayImage,
        hasPresignedUrl,
        getCurrentUser,
        fetchSubServices
    };
};

export default useServiceHDSModal;